import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AuthInitializer from './components/AuthInitializer';
import PlatformShell from './layouts/PlatformShell';
import WorkspacePage from './pages/parivartan/WorkspacePage';
import PlatformAuditPage from './pages/parivartan/PlatformAuditPage';
import ApplicationsPage from './pages/parivartan/ApplicationsPage';
import ApprovalsPage from './pages/parivartan/ApprovalsPage';
import ApprovalRecordPage from './pages/parivartan/ApprovalRecordPage';
import RateMasterPage from './pages/parivartan/tender/RateMasterPage';
import RateFormPage from './pages/parivartan/tender/RateFormPage';
import CompanyOnboardingPage from './pages/parivartan/CompanyOnboardingPage';
import UsersAdminPage from './pages/parivartan/UsersAdminPage';
import LoginHistoryPage from './pages/parivartan/LoginHistoryPage';
import UserFormPage from './pages/parivartan/settings/UserFormPage';
import RolesAdminPage from './pages/parivartan/RolesAdminPage';
import RoleFormPage from './pages/parivartan/settings/RoleFormPage';
import PlatformSettingsPage from './pages/parivartan/PlatformSettingsPage';
import WorkspaceSupportPage from './pages/parivartan/WorkspaceSupportPage';
import WorkspaceSupportNewPage from './pages/parivartan/WorkspaceSupportNewPage';
import AppSettingsPage from './pages/parivartan/settings/AppSettingsPage';
import EmailSettingsPage from './pages/parivartan/settings/EmailSettingsPage';
import DropdownCatalogPage from './pages/parivartan/settings/DropdownCatalogPage';
import DropdownValuesPage from './pages/parivartan/settings/DropdownValuesPage';
import DiligenceTemplatesPage from './pages/parivartan/tender/DiligenceTemplatesPage';
import DropdownValueFormPage from './pages/parivartan/settings/DropdownValueFormPage';
import NotificationsSettingsPage from './pages/parivartan/settings/NotificationsSettingsPage';
import TenderAppHome from './pages/parivartan/tender/TenderAppHome';
import TenderRegisterPage from './pages/parivartan/tender/TenderRegisterPage';
import TenderDetailWorkspace from './pages/parivartan/tender/TenderDetailWorkspace';
import KnowledgeBankPage from './pages/parivartan/tender/KnowledgeBankPage';
import KnowledgeRecordPage from './pages/parivartan/tender/KnowledgeRecordPage';
import PersonnelPage from './pages/parivartan/tender/PersonnelPage';
import PersonnelFormPage from './pages/parivartan/tender/PersonnelFormPage';
import ReportsPage from './pages/parivartan/tender/ReportsPage';
import MastersPage from './pages/parivartan/tender/MastersPage';
import MasterUploadPage from './pages/parivartan/tender/MasterUploadPage';
import CustomerListPage from './pages/parivartan/tender/CustomerListPage';
import CustomerFormPage from './pages/parivartan/tender/CustomerFormPage';
import ServiceListPage from './pages/parivartan/tender/ServiceListPage';
import ServiceFormPage from './pages/parivartan/tender/ServiceFormPage';
import SectorListPage from './pages/parivartan/tender/SectorListPage';
import SectorFormPage from './pages/parivartan/tender/SectorFormPage';
import TenderTypeListPage from './pages/parivartan/tender/TenderTypeListPage';
import TenderTypeFormPage from './pages/parivartan/tender/TenderTypeFormPage';
import SourceListPage from './pages/parivartan/tender/SourceListPage';
import SourceFormPage from './pages/parivartan/tender/SourceFormPage';
import TimesheetsPage from './pages/parivartan/tender/TimesheetsPage';
import TimesheetFormPage from './pages/parivartan/tender/TimesheetFormPage';
import TimesheetWeekPage from './pages/parivartan/tender/TimesheetWeekPage';
import TimesheetActivitiesPage from './pages/parivartan/tender/TimesheetActivitiesPage';
import TimesheetRulesPage from './pages/parivartan/settings/TimesheetRulesPage';
import OrganisationSettingsPage from './pages/parivartan/settings/OrganisationSettingsPage';
import PursuitRulesPage from './pages/parivartan/settings/PursuitRulesPage';
import SecuritiesPage from './pages/parivartan/tender/SecuritiesPage';
import RequireAccess from './components/workspace/RequireAccess';
import { APPROVAL_ACCESS, KNOWLEDGE_ACCESS, MASTERS_ACCESS, REGISTER_ACCESS, SECURITIES_ACCESS, SETTINGS_ACCESS, TIMESHEET_ACCESS } from './config/workspaceAccess';
import PartnersPage from './pages/parivartan/tender/PartnersPage';
import IssuersPage from './pages/parivartan/tender/IssuersPage';
import DecisionProfilesPage from './pages/parivartan/tender/DecisionProfilesPage';
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import UserProfile from './pages/UserProfile';
import MyWork from './pages/MyWork';
import Pipeline from './pages/tender-intelligence/Pipeline';
import PipelineTenderPage from './pages/tender-intelligence/PipelineTenderPage';
import { useSelector } from 'react-redux';

const LegacyPipelineRedirect = () => {
  const location = useLocation();
  const tail = location.pathname.replace(/^\/tender-intelligence\/pipeline\/?/, '');
  if (tail === 'new') {
    return <Navigate to="/apps/tender/register/new" replace state={location.state} />;
  }
  const editMatch = tail.match(/^([^/]+)\/edit\/?$/);
  if (editMatch) {
    return (
      <Navigate
        to={`/apps/tender/register/${editMatch[1]}/edit`}
        replace
        state={location.state}
      />
    );
  }
  const viewMatch = tail.match(/^([^/]+)\/?$/);
  if (viewMatch) {
    return <Navigate to={`/apps/tender/register/${viewMatch[1]}`} replace />;
  }
  return <Navigate to="/apps/tender/register" replace />;
};

const Protected = ({ children }) => {
  const { isAuthenticated, isInitialized } = useSelector((s) => s.auth);
  if (!isInitialized) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const PlatformApp = () => (
  <AuthInitializer>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/register-respondent" element={<Navigate to="/login" replace />} />
      <Route path="/respond/*" element={<Navigate to="/workspace" replace />} />
      <Route path="/issued-rfps/*" element={<Navigate to="/apps/tender" replace />} />
      <Route path="/tender-discovery/*" element={<Navigate to="/workspace" replace />} />
      <Route path="/integrations" element={<Navigate to="/platform/settings" replace />} />
      <Route path="/governance-audit" element={<Navigate to="/platform/audit" replace />} />
      <Route path="/dashboard" element={<Navigate to="/workspace" replace />} />

      <Route
        path="/"
        element={
          <Protected>
            <PlatformShell mode="platform" />
          </Protected>
        }
      >
        <Route index element={<Navigate to="/workspace" replace />} />
        <Route path="workspace" element={<WorkspacePage />} />
        <Route path="my-work" element={<MyWork />} />
        <Route path="profile" element={<UserProfile />} />
        <Route path="platform/company" element={<CompanyOnboardingPage />} />
        <Route path="platform/users" element={<RequireAccess allow={['platform.users.manage', 'users.view']} admins><UsersAdminPage /></RequireAccess>} />
        <Route path="platform/users/login-history" element={<RequireAccess allow={['platform.users.manage', 'users.view']} admins><LoginHistoryPage /></RequireAccess>} />
        <Route path="platform/users/new" element={<RequireAccess allow={['platform.users.manage', 'users.create', 'users.update']} admins><UserFormPage /></RequireAccess>} />
        <Route path="platform/users/:userId/edit" element={<RequireAccess allow={['platform.users.manage', 'users.create', 'users.update']} admins><UserFormPage /></RequireAccess>} />
        <Route path="platform/roles" element={<RequireAccess allow={['platform.roles.manage', 'roles.view']} admins><RolesAdminPage /></RequireAccess>} />
        <Route path="platform/roles/new" element={<RequireAccess allow={['platform.roles.manage', 'roles.create', 'roles.update']} admins><RoleFormPage /></RequireAccess>} />
        <Route path="platform/roles/:roleKey/edit" element={<RequireAccess allow={['platform.roles.manage', 'roles.create', 'roles.update']} admins><RoleFormPage /></RequireAccess>} />
        <Route path="platform/applications" element={<ApplicationsPage />} />
        <Route path="platform/settings" element={<RequireAccess allow={SETTINGS_ACCESS}><AppSettingsPage /></RequireAccess>} />
        <Route path="platform/settings/users" element={<RequireAccess allow={['platform.users.manage', 'users.view']} admins><UsersAdminPage /></RequireAccess>} />
        <Route path="platform/settings/users/login-history" element={<RequireAccess allow={['platform.users.manage', 'users.view']} admins><LoginHistoryPage /></RequireAccess>} />
        <Route path="platform/settings/users/new" element={<RequireAccess allow={['platform.users.manage', 'users.create', 'users.update']} admins><UserFormPage /></RequireAccess>} />
        <Route path="platform/settings/users/:userId/edit" element={<RequireAccess allow={['platform.users.manage', 'users.create', 'users.update']} admins><UserFormPage /></RequireAccess>} />
        <Route path="platform/settings/roles" element={<RequireAccess allow={['platform.roles.manage', 'roles.view']} admins><RolesAdminPage /></RequireAccess>} />
        <Route path="platform/settings/roles/new" element={<RequireAccess allow={['platform.roles.manage', 'roles.create', 'roles.update']} admins><RoleFormPage /></RequireAccess>} />
        <Route path="platform/settings/roles/:roleKey/edit" element={<RequireAccess allow={['platform.roles.manage', 'roles.create', 'roles.update']} admins><RoleFormPage /></RequireAccess>} />
        <Route path="platform/settings/email" element={<EmailSettingsPage />} />
        <Route path="platform/settings/notifications" element={<NotificationsSettingsPage />} />
        <Route path="platform/settings/timesheet" element={<TimesheetRulesPage />} />
        <Route path="platform/settings/organisation" element={<OrganisationSettingsPage />} />
        <Route path="platform/settings/ai" element={<PlatformSettingsPage />} />
        <Route path="platform/settings/dropdowns" element={<DropdownCatalogPage />} />
        <Route path="platform/settings/dropdowns/:catalog/new" element={<DropdownValueFormPage />} />
        <Route path="platform/settings/dropdowns/:catalog/:valueId/edit" element={<DropdownValueFormPage />} />
        <Route path="platform/settings/dropdowns/:catalog/:valueId" element={<DropdownValueFormPage />} />
        <Route path="platform/settings/dropdowns/:catalog" element={<DropdownValuesPage />} />
        <Route path="platform/audit" element={<PlatformAuditPage />} />
        <Route path="platform/support" element={<WorkspaceSupportPage />} />
        <Route path="platform/support/new" element={<WorkspaceSupportNewPage />} />
      </Route>

      <Route
        path="/apps/tender"
        element={
          <Protected>
            <PlatformShell mode="tender" />
          </Protected>
        }
      >
        <Route index element={<RequireAccess allow={['tender.read', 'tenders.view']}><TenderAppHome /></RequireAccess>} />
        <Route path="register" element={<RequireAccess allow={REGISTER_ACCESS}><TenderRegisterPage /></RequireAccess>} />
        <Route path="register/new" element={<RequireAccess allow={['tender.create', 'tenders.create']}><PipelineTenderPage /></RequireAccess>} />
        <Route path="register/:tenderId/edit" element={<RequireAccess allow={['tender.update', 'tenders.update']} admins><PipelineTenderPage /></RequireAccess>} />
        <Route path="register/:tenderId" element={<RequireAccess allow={REGISTER_ACCESS}><TenderDetailWorkspace /></RequireAccess>} />
        <Route path="knowledge" element={<RequireAccess allow={KNOWLEDGE_ACCESS}><KnowledgeBankPage /></RequireAccess>} />
        <Route path="knowledge/credentials/new" element={<KnowledgeRecordPage />} />
        <Route path="knowledge/credentials/:recordId/edit" element={<KnowledgeRecordPage />} />
        <Route path="knowledge/credentials/:recordId" element={<KnowledgeRecordPage />} />
        <Route path="knowledge/projects/new" element={<KnowledgeRecordPage />} />
        <Route path="knowledge/projects/:recordId/edit" element={<KnowledgeRecordPage />} />
        <Route path="knowledge/projects/:recordId" element={<KnowledgeRecordPage />} />
        <Route path="knowledge/reusable/new" element={<KnowledgeRecordPage />} />
        <Route path="knowledge/reusable/:recordId/edit" element={<KnowledgeRecordPage />} />
        <Route path="knowledge/reusable/:recordId" element={<KnowledgeRecordPage />} />
        <Route path="personnel" element={<RequireAccess allow={['tender.read', 'personnel.view']}><PersonnelPage /></RequireAccess>} />
        <Route path="personnel/new" element={<PersonnelFormPage />} />
        <Route path="personnel/:personId/edit" element={<PersonnelFormPage />} />
        <Route path="personnel/:personId" element={<PersonnelFormPage />} />
        <Route path="timesheets" element={<RequireAccess allow={TIMESHEET_ACCESS}><TimesheetsPage /></RequireAccess>} />
        <Route path="securities" element={<RequireAccess allow={SECURITIES_ACCESS}><SecuritiesPage /></RequireAccess>} />
        <Route element={<RequireAccess allow={TIMESHEET_ACCESS} />}>
          <Route path="timesheets/week" element={<TimesheetWeekPage />} />
          <Route path="timesheets/new" element={<TimesheetFormPage />} />
          <Route path="timesheets/:timesheetId/edit" element={<TimesheetFormPage />} />
          <Route path="timesheets/:timesheetId" element={<TimesheetFormPage />} />
        </Route>
        <Route path="approvals" element={<RequireAccess allow={APPROVAL_ACCESS}><ApprovalsPage /></RequireAccess>} />
        <Route path="approvals/:workItemId/decide" element={<ApprovalRecordPage />} />
        <Route path="approvals/:workItemId" element={<ApprovalRecordPage />} />
        <Route path="rates" element={<Navigate to="/apps/tender/masters/rates" replace />} />
        <Route path="masters" element={<RequireAccess allow={MASTERS_ACCESS} admins><MastersPage /></RequireAccess>} />
        <Route element={<RequireAccess allow={MASTERS_ACCESS} admins />}>
          <Route path="masters/upload" element={<MasterUploadPage />} />
          <Route path="masters/customers" element={<CustomerListPage />} />
          <Route path="masters/customers/new" element={<CustomerFormPage />} />
          <Route path="masters/customers/:customerId/edit" element={<CustomerFormPage />} />
          <Route path="masters/customers/:customerId" element={<CustomerFormPage />} />
          <Route path="masters/services" element={<ServiceListPage />} />
          <Route path="masters/services/new" element={<ServiceFormPage />} />
          <Route path="masters/services/:serviceId/edit" element={<ServiceFormPage />} />
          <Route path="masters/services/:serviceId" element={<ServiceFormPage />} />
          <Route path="masters/sectors" element={<SectorListPage />} />
          <Route path="masters/sectors/new" element={<SectorFormPage />} />
          <Route path="masters/sectors/:sectorId/edit" element={<SectorFormPage />} />
          <Route path="masters/sectors/:sectorId" element={<SectorFormPage />} />
          <Route path="masters/tender-types" element={<TenderTypeListPage />} />
          <Route path="masters/tender-types/new" element={<TenderTypeFormPage />} />
          <Route path="masters/tender-types/:typeId/edit" element={<TenderTypeFormPage />} />
          <Route path="masters/tender-types/:typeId" element={<TenderTypeFormPage />} />
          <Route path="masters/sources" element={<SourceListPage />} />
          <Route path="masters/sources/new" element={<SourceFormPage />} />
          <Route path="masters/sources/:sourceId/edit" element={<SourceFormPage />} />
          <Route path="masters/sources/:sourceId" element={<SourceFormPage />} />
          <Route path="masters/timesheet-activities" element={<TimesheetActivitiesPage />} />
          <Route path="masters/issuers" element={<IssuersPage />} />
          <Route path="masters/partners" element={<PartnersPage />} />
          <Route path="masters/decision-profiles" element={<DecisionProfilesPage />} />
          <Route path="masters/lists/:catalog/new" element={<DropdownValueFormPage />} />
          <Route path="masters/lists/:catalog/:valueId/edit" element={<DropdownValueFormPage />} />
          <Route path="masters/lists/:catalog/:valueId" element={<DropdownValueFormPage />} />
          <Route path="masters/lists/:catalog" element={<DropdownValuesPage />} />
          <Route path="masters/diligence" element={<DiligenceTemplatesPage />} />
          <Route path="masters/rates" element={<RateMasterPage />} />
          <Route path="masters/rates/new" element={<RateFormPage />} />
          <Route path="masters/rates/:rateId/edit" element={<RateFormPage />} />
          <Route path="masters/rates/:rateId" element={<RateFormPage />} />
        </Route>
        <Route path="reports" element={<RequireAccess allow={['tender.reports.read', 'reports.view']}><ReportsPage /></RequireAccess>} />
        <Route path="support" element={<WorkspaceSupportPage />} />
        <Route path="support/new" element={<WorkspaceSupportNewPage />} />
        <Route path="my-work" element={<MyWork />} />
        <Route path="settings" element={<RequireAccess allow={SETTINGS_ACCESS}><AppSettingsPage /></RequireAccess>} />
        <Route path="settings/users" element={<RequireAccess allow={['platform.users.manage', 'users.view']} admins><UsersAdminPage /></RequireAccess>} />
        <Route path="settings/users/login-history" element={<RequireAccess allow={['platform.users.manage', 'users.view']} admins><LoginHistoryPage /></RequireAccess>} />
        <Route path="settings/users/new" element={<RequireAccess allow={['platform.users.manage', 'users.create', 'users.update']} admins><UserFormPage /></RequireAccess>} />
        <Route path="settings/users/:userId/edit" element={<RequireAccess allow={['platform.users.manage', 'users.create', 'users.update']} admins><UserFormPage /></RequireAccess>} />
        <Route path="settings/roles" element={<RequireAccess allow={['platform.roles.manage', 'roles.view']} admins><RolesAdminPage /></RequireAccess>} />
        <Route path="settings/roles/new" element={<RequireAccess allow={['platform.roles.manage', 'roles.create', 'roles.update']} admins><RoleFormPage /></RequireAccess>} />
        <Route path="settings/roles/:roleKey/edit" element={<RequireAccess allow={['platform.roles.manage', 'roles.create', 'roles.update']} admins><RoleFormPage /></RequireAccess>} />
        <Route path="settings/email" element={<RequireAccess allow={MASTERS_ACCESS} admins><EmailSettingsPage /></RequireAccess>} />
        <Route path="settings/notifications" element={<RequireAccess allow={['platform.company.manage', 'company.view']} admins><NotificationsSettingsPage /></RequireAccess>} />
        <Route path="settings/timesheet" element={<RequireAccess allow={MASTERS_ACCESS} admins><TimesheetRulesPage /></RequireAccess>} />
        <Route path="settings/organisation" element={<RequireAccess allow={SETTINGS_ACCESS} admins><OrganisationSettingsPage /></RequireAccess>} />
        <Route path="settings/pursuit" element={<RequireAccess allow={MASTERS_ACCESS} admins><PursuitRulesPage /></RequireAccess>} />
        <Route path="settings/ai" element={<RequireAccess allow={['platform.ai.configure', 'ai.view']} admins><PlatformSettingsPage /></RequireAccess>} />
        <Route element={<RequireAccess allow={MASTERS_ACCESS} admins />}>
          <Route path="settings/dropdowns" element={<DropdownCatalogPage />} />
          <Route path="settings/dropdowns/:catalog/new" element={<DropdownValueFormPage />} />
          <Route path="settings/dropdowns/:catalog/:valueId/edit" element={<DropdownValueFormPage />} />
          <Route path="settings/dropdowns/:catalog/:valueId" element={<DropdownValueFormPage />} />
          <Route path="settings/dropdowns/:catalog" element={<DropdownValuesPage />} />
        </Route>
        <Route path="profile" element={<UserProfile />} />
      </Route>

      <Route path="tender-intelligence/pipeline" element={<Protected><Pipeline /></Protected>} />
      <Route path="tender-intelligence/pipeline/*" element={<Protected><LegacyPipelineRedirect /></Protected>} />

      <Route path="*" element={<Navigate to="/workspace" replace />} />
    </Routes>
  </AuthInitializer>
);

export default PlatformApp;
